// "Update Code" confirmation + progress, shared by all three scopes (device,
// room, location) and both UI shells.
//
// The flow is deliberately three-stage — counting, confirming, sending —
// because this is not undoable and the devices reboot:
//
//   1. COUNT   gather the devices in scope and split live vs offline. Offline
//              devices are NOT sent to (they'd drop the message and look
//              updated), and are listed so it's obvious what was skipped.
//   2. CONFIRM show the counts AND THE EXACT PAYLOAD that will be published, so
//              the operator can see the literal bytes before a fleet of devices
//              is told to reflash itself.
//   3. SEND    publish one device at a time, spaced, with progress — a location
//              rollout takes a while and silence would look like a hang.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box,
  LinearProgress, CircularProgress, Chip, Alert,
} from '@mui/material';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import { useApp } from '../../core/state/AppContext';
import {
  collectLocationDevices, collectRoomDevices, splitByLiveness,
  sendFirmwareUpdate, previewPayload, FIRMWARE_UPDATE_COMMAND,
} from '../../core/devices/firmwareUpdate';

// scope: { type: 'device'|'room'|'location', id, name, device? }
export default function UpdateCodeDialog({ open, scope, onClose }) {
  const { userId } = useApp();
  const [phase, setPhase] = useState('counting'); // counting | confirm | sending | done
  const [live, setLive] = useState([]);
  const [offline, setOffline] = useState([]);
  const [method, setMethod] = useState('mqtt');
  const [sent, setSent] = useState(0);

  const load = useCallback(async () => {
    setPhase('counting');
    setSent(0);
    let devices = [];
    if (scope?.type === 'device') devices = scope.device ? [scope.device] : [];
    else if (scope?.type === 'room') devices = await collectRoomDevices(userId, scope.id);
    else if (scope?.type === 'location') devices = await collectLocationDevices(userId, scope.id);
    // Awaited: liveness is the same cached status the tiles show, so the dialog
    // and the dot can never disagree.
    const split = await splitByLiveness(devices);
    setLive(split.live);
    setOffline(split.offline);
    setMethod(split.method);
    setPhase('confirm');
  }, [scope, userId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const run = async () => {
    setPhase('sending');
    await sendFirmwareUpdate(live, userId, (n) => setSent(n));
    setPhase('done');
  };

  const scopeLabel = scope?.type === 'device' ? 'device'
    : scope?.type === 'room' ? 'room' : 'location';

  const payload = previewPayload(
    scope?.type === 'device' ? (scope.device && scope.device.UniqueNumber) : null,
    userId,
  );

  return (
    <Dialog open={open} onClose={phase === 'sending' ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SystemUpdateAltIcon />
        Update Code
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {`Tells each device to download its firmware from the server and reflash itself. `}
          {scope?.name ? <b>{scope.name}</b> : null}
          {` (${scopeLabel})`}
        </Typography>

        {phase === 'counting' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Asking the devices if they are there…</Typography>
          </Box>
        )}

        {phase === 'confirm' && (
          <>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
              <Chip color="success" size="small" label={`${live.length} online — will update`} />
              {offline.length > 0 && (
                <Chip size="small" label={`${offline.length} offline — skipped`} />
              )}
            </Box>

            {offline.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                {`Skipped: ${offline.map((d) => d.DeviceName || d.UniqueNumber).join(', ')}. `}
                They stay on their current firmware — run this again when they are back online.
              </Typography>
            )}

            {/* The exact bytes — last look before a fleet reflashes itself. */}
            <Alert severity="warning" sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ display: 'block' }}>
                Command that will be published to each device:
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: 'monospace', fontWeight: 700, wordBreak: 'break-all' }}
              >
                {payload}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                Devices will download new firmware and reboot. This cannot be undone.
              </Typography>
            </Alert>

            {/* When the count is zero, say HOW it was counted. A bare "0 online"
                next to a device the user can see working is the report that
                sent us chasing this twice. */}
            {live.length === 0 && (
              <Typography variant="body2" color="error">
                {method === 'mqtt'
                  ? `No device in this ${scopeLabel} answered, and none has reported `
                    + 'to the server in the last minute. A device that is powered and '
                    + 'on the network replies in under a second — check power and '
                    + 'Wi-Fi, then reopen this.'
                  : 'The broker could not be reached, so this fell back to the last '
                    + 'status the server recorded, and none of these devices is '
                    + 'recent. Check this device’s own connection first.'}
              </Typography>
            )}

            {method === 'status' && live.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Broker unreachable — this list is from the server’s last recorded
                status, not from the devices themselves.
              </Typography>
            )}
          </>
        )}

        {(phase === 'sending' || phase === 'done') && (
          <Box sx={{ py: 1 }}>
            <LinearProgress
              variant="determinate"
              value={live.length ? (sent / live.length) * 100 : 100}
              sx={{ mb: 1 }}
            />
            <Typography variant="body2">
              {phase === 'done'
                ? `Sent to ${sent} device${sent === 1 ? '' : 's'}.`
                : `Sending… ${sent} of ${live.length}`}
            </Typography>
            {phase === 'done' && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Devices download and reflash on their own; they will drop offline
                briefly while they do. There is no acknowledgement, so check the
                online dots in a minute or two.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {phase === 'confirm' && <Button onClick={onClose}>Cancel</Button>}
        {phase === 'confirm' && (
          <Button variant="contained" color="warning" disabled={live.length === 0} onClick={run}>
            {`Update ${live.length} device${live.length === 1 ? '' : 's'}`}
          </Button>
        )}
        {phase === 'done' && <Button variant="contained" onClick={onClose}>Close</Button>}
        {phase === 'counting' && <Button onClick={onClose}>Cancel</Button>}
      </DialogActions>
    </Dialog>
  );
}

export { FIRMWARE_UPDATE_COMMAND };
